import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import { LT_ROUTE } from 'bl/loggers.bl';
import { fromSalonListObjectToSalonsInfo } from 'bl/salon/fromSalonListObjectToSalonsInfo.bl';
import CodeVerification from 'components/onboarding/CodeVerification.component';
import { QuickSetupTypeWrap } from 'components/onboarding/quickSetupType/QuickSetupTypeWrap.component';
import { RegisterPage } from 'components/onboarding/RegisterPage.component';
import { SalonSetupInfo } from 'components/onboarding/SalonSetupInfo.component';
import { SalonSetupPage } from 'components/onboarding/SalonSetupPage.component';
import LINK from 'constants/link.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectSalonList } from 'selectors/salonInfo.selectors';
import { COGNITO_STATE, selectCognitoState } from 'utils/cognito.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import { isNotValidId, isValidId } from 'utils/predicates/fields.util';

import PrivateRoute from 'lib/PrivateRoute';
import CategoriesScreenWrap from 'onboarding/categories/CategoriesScreenWrap';
import OpeningHoursWrap from 'onboarding/openingHours/OpeningHoursWrap';
import AddRoleWrap from 'onboarding/roles/AddRoleWrap';
import RolesScreenWrap from 'onboarding/roles/RolesScreenWrap';
import AddServiceWrap from 'onboarding/services/AddServiceWrap';
import EditCategoryWrap from 'onboarding/services/EditCategoryWrap';
import ServicesScreenWrap from 'onboarding/services/ServicesScreenWrap';
import AddStylistWrap from 'onboarding/stylists/AddStylistWrap';
import StylistsScreenWrap from 'onboarding/stylists/StylistsScreenWrap';

import CN from './SignupRoutes.module.scss';

const CTAG = 'SignupRoutes';
const L = logger([CTAG, LT_ROUTE]);

export const SignupRoutes = () => {
  const salonId = useCurrentSalonId();
  const cognitoState = useSelector(selectCognitoState);
  const salonList = useSelector(selectSalonList);
  const isLoggedIn = COGNITO_STATE.in === cognitoState;

  const { isOk, hasRole } = useMemo(() => fromSalonListObjectToSalonsInfo(salonList), [salonList]);

  if (isLoggedIn && isNotValidId(salonId) && isOk && hasRole) {
    const pathname = linkTo({ pattern: LINK.salonPick });
    L.info('User is already logged in, redirecting to salon picker:', pathname, '...');

    return <Redirect to={{ pathname }} />;
  }

  if (isLoggedIn && isValidId(salonId) && isOk && hasRole) {
    const pathname = linkTo({ pattern: LINK.salonToday, params: { salonId } });
    L.info("User is already logged in, redirecting to today's scheduler:", pathname, '...');

    return <Redirect to={{ pathname }} />;
  }

  return (
    <div data-bem="SignupRoutes" className={CN.component}>
      <Switch>
        {/* NOTE: these routes should not be under PrivateRoute since that one will keep redirecting to itself */}
        <Route exact path={LINK.onboarding} component={RegisterPage} />
        <Route path={LINK.onboardingSms} component={CodeVerification} />

        {/* PrivateRoute has logic that redirects based in Cognito state */}
        <PrivateRoute path={LINK.onboardingInfo} component={SalonSetupInfo} />
        <PrivateRoute path={LINK.onboardingType} component={QuickSetupTypeWrap} />
        <PrivateRoute path={LINK.onboardingSalon} component={SalonSetupPage} />

        {/* // NOTE: old routes, not in use with the new sign up UI */}
        <PrivateRoute path={LINK.onboardingRoles} component={RolesScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddRole} component={AddRoleWrap} />
        <PrivateRoute path={LINK.onboardingCategories} component={CategoriesScreenWrap} />
        <PrivateRoute path={LINK.onboardingServices} component={ServicesScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddService} component={AddServiceWrap} />
        <PrivateRoute path={LINK.onboardingStylists} component={StylistsScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddStylist} component={AddStylistWrap} />
        <PrivateRoute path={LINK.onboardingEditCategory} component={EditCategoryWrap} />
        <PrivateRoute path={LINK.onboardingOpeningHours} component={OpeningHoursWrap} />
      </Switch>
    </div>
  );
};
